'use client';
import { notFound } from 'next/navigation';

import { Card } from '@uikit';

import { useOptimisticUpdateSelfCoverMutation, usePublicUserQuery } from '@query';

import { useAuth } from '@shared/common/providers/AuthProvider';
import displayErrorToast from '@shared/helpers/displayErrorToast';

import MembershipTierBanner from '@store:web/components/common/MembershipTierBanner/MembershipTierBanner';
import ProfileBanner from '@store:web/components/common/ProfileBanner/ProfileBanner';
import PageHeader from '@store:web/components/layout/PageHeader/PageHeader';

import UserInfo from './UserInfo';
import UserProjects from './UserProjects';

const UserPage = ({ userId }: { userId: string }) => {
  const { user } = useAuth();
  const { data } = usePublicUserQuery({ searchParams: { id: userId } });
  const { mutateAsync: updateSelfCover } = useOptimisticUpdateSelfCoverMutation();

  if (!data) {
    return notFound();
  }

  const handleProfileBannerChange = async (fileHash: string) => {
    try {
      await updateSelfCover(fileHash);
    } catch (error) {
      displayErrorToast({ error });
    }
  };

  const handleProfileBannerDelete = async () => {
    try {
      await updateSelfCover(null);
    } catch (error) {
      displayErrorToast({ error });
    }
  };

  return (
    <>
      <PageHeader
        page="user"
        disableBanner
        extendBreadcrumbs={[
          {
            label: `@${data?.attributes.magicId}`,
            pathname: `/user/${data?.attributes.magicId}`,
          },
        ]}
      />
      <main className="v2-container">
        <Card className="public-profile-card rounded-2xl overflow-hidden" isDefaultPadding={false}>
          <ProfileBanner
            width={1650}
            height={280}
            isEditable={data.id === user?.id}
            src={data.id === user?.id ? user.attributes.cover : data.attributes.cover}
            onUpdate={handleProfileBannerChange}
            onDelete={handleProfileBannerDelete}
          />

          <MembershipTierBanner
            tier={data?.membershipTier?.attributes?.key}
            className="max-md:mb-3 md:px-[160px] lg:px-[320px]"
          />

          <div className="relative flex justify-between items-start flex-col md:flex-row px-4 md:px-0 lg:pl-[100px] lg:pr-[40px] md:pt-[52px] md:pl-[20px] md:pr-[20px] gap-8 pb-10">
            <UserInfo data={data} />
            <UserProjects data={data} />
          </div>
        </Card>
      </main>
    </>
  );
};

export default UserPage;
